import {
  mergeIncomingTalkCluster,
  planIncomingTalkClusterPrune,
  type IncomingTalkClusterWire,
} from '../shared/peer-talk-delivery';
import type { P2PRuntimeFlags } from '../shared/p2p-runtime';
import type { SubscribablePrivateStore } from './ports';

type StoredIncomingTalkCluster = Omit<IncomingTalkClusterWire, 'senders' | 'talkIds' | 'identityAliases'> & {
  senders?: IncomingTalkClusterWire['senders'];
  talkIds?: IncomingTalkClusterWire['talkIds'];
  identityAliases?: IncomingTalkClusterWire['identityAliases'];
  sendersJson?: string;
  talkIdsJson?: string;
  identityAliasesJson?: string;
};

type OwnerIncomingTalkEnvelope = {
  version: 1;
  clustersJson: string;
  updatedAt: string;
};

const ownerEnvelopeWriteQueues = new WeakMap<SubscribablePrivateStore, Promise<void>>();
const OWNER_INCOMING_TALK_PRIVATE_KEY = 'incomingTalkClusters';

async function readOwnerEnvelopeClusters(gunService: SubscribablePrivateStore): Promise<IncomingTalkClusterWire[]> {
  let raw: OwnerIncomingTalkEnvelope | null;
  try {
    raw = await gunService.getPrivate(OWNER_INCOMING_TALK_PRIVATE_KEY) as OwnerIncomingTalkEnvelope | null;
  } catch {
    // First delivery on a device has no owner envelope yet; that is the normal empty state.
    return [];
  }
  if (!raw || raw.version !== 1 || typeof raw.clustersJson !== 'string') return [];
  try {
    const stored = JSON.parse(raw.clustersJson) as StoredIncomingTalkCluster[];
    return stored.map(hydrateCluster).filter((cluster): cluster is IncomingTalkClusterWire => !!cluster?.identityKey);
  } catch {
    return [];
  }
}

function serializeCluster(cluster: IncomingTalkClusterWire): StoredIncomingTalkCluster {
  const { senders, talkIds, identityAliases, ...rest } = cluster;
  return {
    ...rest,
    sendersJson: JSON.stringify(senders || {}),
    talkIdsJson: JSON.stringify(talkIds || {}),
    identityAliasesJson: JSON.stringify(identityAliases || {}),
  };
}

function hydrateCluster(raw: unknown): IncomingTalkClusterWire | null {
  if (!raw || typeof raw !== 'object') return null;
  const stored = raw as StoredIncomingTalkCluster;
  const parseMap = <T>(json: string | undefined, fallback: T): T => {
    if (!json) return fallback;
    try {
      return JSON.parse(json) as T;
    } catch {
      return fallback;
    }
  };
  return {
    ...stored,
    senders: parseMap(stored.sendersJson, stored.senders || {}),
    talkIds: parseMap(stored.talkIdsJson, stored.talkIds || {}),
    identityAliases: parseMap(stored.identityAliasesJson, stored.identityAliases || {}),
  };
}

export function mirrorIncomingTalkClustersToLocalGun(
  gunService: SubscribablePrivateStore,
  userId: string,
  clusters: unknown[],
  flags: P2PRuntimeFlags,
): void {
  if (!flags.p2pClientTalkMirror || !userId || !Array.isArray(clusters)) return;
  for (const raw of clusters) {
    const cluster = raw as { identityKey?: string };
    if (!cluster?.identityKey) continue;
    void persistIncomingTalkClusterToLocalGun(gunService, raw as IncomingTalkClusterWire).catch((error) => {
      console.warn('[client-incoming-talk-mirror] owner envelope mirror failed:', error);
    });
  }
}

function persistIncomingTalkClusterToLocalGun(
  gunService: SubscribablePrivateStore,
  cluster: IncomingTalkClusterWire,
): Promise<void> {
  return persistIncomingTalkClustersToLocalGun(gunService, [cluster]);
}

/** Key-order-independent: a stored (hydrated) cluster and a freshly merged one list keys differently. */
function clusterFingerprint(cluster: IncomingTalkClusterWire): string {
  const sortKeys = (value: unknown): unknown => {
    if (Array.isArray(value)) return value.map(sortKeys);
    if (!value || typeof value !== 'object') return value;
    return Object.fromEntries(Object.keys(value as Record<string, unknown>).sort()
      .map((key) => [key, sortKeys((value as Record<string, unknown>)[key])]));
  };
  const { senders, talkIds, identityAliases, ...rest } = cluster;
  const plain: Record<string, unknown> = { ...rest };
  delete plain.sendersJson;
  delete plain.talkIdsJson;
  delete plain.identityAliasesJson;
  return JSON.stringify(sortKeys({ ...plain, senders: senders || {}, talkIds: talkIds || {}, identityAliases: identityAliases || {} }));
}

type EnvelopeBatch = { clusters: Map<string, IncomingTalkClusterWire>; done: Promise<void> };
const ownerEnvelopeNextBatch = new WeakMap<SubscribablePrivateStore, EnvelopeBatch>();

/**
 * Every incoming Talk (and every UI refresh) asks for its cluster to be saved, and each save is a
 * read, merge and encrypted rewrite of the whole owner envelope. One queued rewrite per request
 * fell behind under load: six browsers on one host took 1–7 s per rewrite and the queue reached
 * 155 s, so a later save (find-similar-people's seeded Talks) waited until the spec timed out.
 * Requests that arrive while a rewrite is running now join the next one, which writes them all.
 */
function persistIncomingTalkClustersToLocalGun(
  gunService: SubscribablePrivateStore,
  clusters: IncomingTalkClusterWire[],
): Promise<void> {
  let batch = ownerEnvelopeNextBatch.get(gunService);
  if (!batch) {
    const pending = new Map<string, IncomingTalkClusterWire>();
    const done = (ownerEnvelopeWriteQueues.get(gunService) || Promise.resolve())
      .catch(() => undefined)
      .then(() => {
        // From here on, new requests start the next batch.
        if (ownerEnvelopeNextBatch.get(gunService)?.clusters === pending) ownerEnvelopeNextBatch.delete(gunService);
        return writeOwnerEnvelope(gunService, [...pending.values()]);
      });
    batch = { clusters: pending, done };
    ownerEnvelopeNextBatch.set(gunService, batch);
    ownerEnvelopeWriteQueues.set(gunService, done);
  }
  for (const cluster of clusters) batch.clusters.set(cluster.identityKey, cluster);
  return batch.done;
}

async function writeOwnerEnvelope(
  gunService: SubscribablePrivateStore,
  clusters: IncomingTalkClusterWire[],
): Promise<void> {
  const current = await readOwnerEnvelopeClusters(gunService);
  const byIdentity = new Map(current.map((item) => [item.identityKey, item]));
  let changed = false;
  for (const cluster of clusters) {
    const stored = byIdentity.get(cluster.identityKey);
    // Server/UI refreshes mirror their current rows back through this helper. Rewriting an
    // identical row changes updatedAt, fires the owner-envelope subscription, refreshes the
    // UI again, and creates a write/refresh feedback loop (especially costly on Android).
    if (stored && clusterFingerprint(stored) === clusterFingerprint(cluster)) continue;
    byIdentity.set(cluster.identityKey, cluster);
    changed = true;
  }
  if (!changed) return;
  const envelope: OwnerIncomingTalkEnvelope = {
    version: 1,
    clustersJson: JSON.stringify([...byIdentity.values()].map(serializeCluster)),
    updatedAt: new Date().toISOString(),
  };
  await gunService.putPrivate(OWNER_INCOMING_TALK_PRIVATE_KEY, envelope);
  // putPrivate() already applied this envelope to the local Gun graph — the read-back
  // below is a paranoid double-check, not the real commit. In this deployment (relay-only
  // hub, no local persistence) an older store on a freshly-written key could hang for its full
  // multi-second timeout and reject even though the write succeeded, which used to turn a
  // successful incoming-talk mirror into a reported delivery failure. Log and continue.
  try {
    const verified = await gunService.getPrivate(
      OWNER_INCOMING_TALK_PRIVATE_KEY,
    ) as OwnerIncomingTalkEnvelope | null;
    if (verified?.version !== 1 || verified.clustersJson !== envelope.clustersJson) {
      console.warn(`incoming talk envelope read-back inconclusive (continuing — write already committed locally): ${clusters.map((c) => c.identityKey).join(',')}`);
    }
  } catch (error) {
    console.warn(`incoming talk envelope read-back timed out (continuing — write already committed locally): ${clusters.map((c) => c.identityKey).join(',')}`, error);
  }
}

export async function upsertLocalIncomingTalkClusters(
  gunService: SubscribablePrivateStore,
  receiverUserId: string,
  items: Array<{
    talkId: string;
    talkData: Record<string, unknown>;
    senderId: string;
    senderName: string;
  }>,
  flags: P2PRuntimeFlags,
): Promise<IncomingTalkClusterWire[]> {
  const ownerClusters = flags.p2pClientTalkMirror
    ? await readOwnerEnvelopeClusters(gunService)
    : [];
  const byIdentity = new Map(ownerClusters.map((cluster) => [cluster.identityKey, cluster]));
  const results: IncomingTalkClusterWire[] = [];
  for (const params of items) {
    const draft = mergeIncomingTalkCluster(null, params);
    const cluster = mergeIncomingTalkCluster(byIdentity.get(draft.identityKey), params);
    byIdentity.set(cluster.identityKey, cluster);
    results.push(cluster);
  }
  if (flags.p2pClientTalkMirror && results.length > 0) {
    await persistIncomingTalkClustersToLocalGun(gunService, results);
    void pruneIncomingTalkClustersIfNeeded(gunService, receiverUserId, flags).catch((err) => {
      console.warn('[client-incoming-talk-mirror] incoming-talk-cluster prune failed (non-fatal):', receiverUserId, err);
    });
  }
  return results;
}

export async function upsertLocalIncomingTalkCluster(
  gunService: SubscribablePrivateStore,
  receiverUserId: string,
  params: {
    talkId: string;
    talkData: Record<string, unknown>;
    senderId: string;
    senderName: string;
  },
  flags: P2PRuntimeFlags,
  existingCluster?: IncomingTalkClusterWire | null,
): Promise<IncomingTalkClusterWire> {
  const draft = mergeIncomingTalkCluster(existingCluster, params);
  const ownerClusters = flags.p2pClientTalkMirror
    ? await readOwnerEnvelopeClusters(gunService)
    : [];
  const durableExisting = ownerClusters.find((item) => item.identityKey === draft.identityKey) || existingCluster;
  const cluster = mergeIncomingTalkCluster(durableExisting, params);
  if (!flags.p2pClientTalkMirror) return cluster;
  // The mesh ACK must mean the receiver can reopen this talk, not merely that an in-memory
  // callback ran. Await the nested Gun write acknowledgement before handleMeshTalkBody returns.
  // A failure now withholds the mesh ACK, allowing the sender's existing mailbox fallback to
  // retry instead of silently losing the row from the receiver's Talks UI.
  await persistIncomingTalkClusterToLocalGun(gunService, cluster);
  // docs/TODO.md §Y2: fire-and-forget, mirroring the visit-counter prune trigger
  // (WebChatroomService.recordRoomVisit) — every device prunes its own local Gun graph
  // independently, there is no single authoritative pruner under the P2P model.
  void pruneIncomingTalkClustersIfNeeded(gunService, receiverUserId, flags).catch((err) => {
    console.warn('[client-incoming-talk-mirror] incoming-talk-cluster prune failed (non-fatal):', receiverUserId, err);
  });
  return cluster;
}

/**
 * docs/TODO.md §Y2 — once a user's live incoming-talk-cluster count exceeds
 * `DEFAULT_INCOMING_TALK_CLUSTER_MAX_SLOTS`, delete the oldest (by `updatedAt`) outright.
 * No aggregate fold needed here (see `planIncomingTalkClusterPrune`'s doc comment) — a
 * pruned cluster's own Q&A record already survives independently in the Me tab.
 */
export async function pruneIncomingTalkClustersIfNeeded(
  gunService: SubscribablePrivateStore,
  receiverUserId: string,
  flags: P2PRuntimeFlags,
): Promise<void> {
  if (!flags.p2pClientTalkMirror || !receiverUserId) return;
  const clusters = await collectLocalIncomingTalkClusters(gunService, receiverUserId, flags);
  const plan = planIncomingTalkClusterPrune(clusters);
  if (plan.clustersToPrune.length === 0) return;
  const prunedKeys = new Set(plan.clustersToPrune.map((cluster) => cluster.identityKey));
  const remaining = clusters.filter((cluster) => !prunedKeys.has(cluster.identityKey));
  await gunService.putPrivate(OWNER_INCOMING_TALK_PRIVATE_KEY, {
    version: 1,
    clustersJson: JSON.stringify(remaining.map(serializeCluster)),
    updatedAt: new Date().toISOString(),
  } satisfies OwnerIncomingTalkEnvelope);
}

export async function collectLocalIncomingTalkClusters(
  gunService: SubscribablePrivateStore,
  _receiverUserId: string,
  _flags: P2PRuntimeFlags,
): Promise<IncomingTalkClusterWire[]> {
  return readOwnerEnvelopeClusters(gunService);
}

export function subscribeLocalIncomingTalkClusters(
  gunService: SubscribablePrivateStore,
  _receiverUserId: string,
  _flags: P2PRuntimeFlags,
  handler: (cluster: IncomingTalkClusterWire, id: string) => void,
): () => void {
  const onOwnerEnvelope = (raw: OwnerIncomingTalkEnvelope | null | undefined) => {
    if (!raw || raw.version !== 1 || typeof raw.clustersJson !== 'string') return;
    try {
      const rows = JSON.parse(raw.clustersJson) as StoredIncomingTalkCluster[];
      for (const row of rows) {
        const cluster = hydrateCluster(row);
        if (cluster?.identityKey) handler(cluster, cluster.identityKey);
      }
    } catch {
      // Ignore a corrupt/incomplete replication frame; the next valid envelope update retries.
    }
  };
  return gunService.subscribePrivate(OWNER_INCOMING_TALK_PRIVATE_KEY, onOwnerEnvelope);
}
